// =============================================================================
// RingProgress.tsx
// Single-arc progress ring with a percentage in the centre. Round linecap is
// fine here — single arc, no joints to worry about. Animation: stroke-
// dashoffset eases down from full circumference to the target on mount.
// =============================================================================

import React, { useEffect, useState } from 'react';

interface RingProgressProps {
  pct: number;
  label: string;
  color: string;
  size?: number;
}

const STROKE_WIDTH = 6;

export function RingProgress({ pct, label, color, size = 70 }: RingProgressProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 50); return () => clearTimeout(t); }, []);

  // r leaves stroke-width + 4px breathing room inside the viewBox — same
  // formula as MiniDonut so both charts have a consistent clipping margin.
  const r = (size - STROKE_WIDTH - 4) / 2;
  const circ = 2 * Math.PI * r;
  const offset = mounted ? circ * (1 - Math.min(pct, 100) / 100) : circ;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} preserveAspectRatio="xMidYMid meet" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: 'var(--surface2)' }} strokeWidth={STROKE_WIDTH} vectorEffect="non-scaling-stroke" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(0.25,0.46,0.45,0.94)' }}
        />
      </svg>
      <div style={{ position: 'relative', marginTop: -(size / 2) - 8, fontSize: 14, fontWeight: 700, color }}>{pct}%</div>
      <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', marginTop: size / 2 - 18 }}>{label}</div>
    </div>
  );
}
